import type {
  EntryPointStatus,
  RotateOriginSecretRequest,
  ActivityEntry,
  AddressPlan,
  Attachment,
  AttachmentListFilter,
  CreateNetworkRequest,
  CreateNetworkRunRequest,
  EligibleDeployment,
  Network,
  NetworkDns,
  NetworkKinds,
  NetworkListItem,
  NetworkValidation,
  PatchNetworkRequest,
  BulkDecideRequestBody,
  BulkDecideResponse,
  DecideRequestBody,
  RequestDecisionKind,
  RequestGroup,
  RequestInstance,
  RequestInstanceKey,
  RequestListFilter,
  ActivityFilter,
  AppConfig,
  AppRestartResult,
  Blueprint,
  BlueprintDocument,
  BlueprintListItem,
  BlueprintVersion,
  BlueprintVersionSummary,
  Changelog,
  CloudAccount,
  CreateCloudAccountRequest,
  CreateDeploymentRequest,
  CreateRunRequest,
  Deployment,
  DeploymentListFilter,
  DeploymentListItem,
  DeploymentResource,
  DeploymentSettings,
  DeploymentValidation,
  DeploymentVariables,
  Diagnostic,
  JsonObject,
  Layout,
  Me,
  Page,
  PageRequest,
  ProblemDetails,
  Provider,
  ResourceSettings,
  ResourceType,
  RevealedSecret,
  RevealRequest,
  Run,
  RunListFilter,
  RunSummary,
  RunEventPage,
  RunVariableChanges,
  SecretMetadata,
  SecretsOverview,
  SecretStore,
  SecretValueSetResult,
  SetSecretRequest,
  UpdateCloudAccountRequest,
  ValidationResult,
  VerificationResult,
  VersionInfo,
} from './types';
import { connection } from './connection';

export const API_PREFIX = '/api/v1';

const DEFAULT_CODES: Record<number, string> = {
  400: 'VALIDATION_FAILED',
  401: 'UNAUTHENTICATED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  412: 'PRECONDITION_FAILED',
  413: 'PAYLOAD_TOO_LARGE',
  428: 'PRECONDITION_REQUIRED',
  429: 'RATE_LIMITED',
  504: 'VERIFICATION_TIMEOUT',
};

/** An RFC 9457 Problem Details response, or a network-level failure (status 0). */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly problem: ProblemDetails;
  /** `Retry-After` in seconds (a 429 or 503 that sent it as a number), else null. */
  readonly retryAfterSeconds: number | null;

  constructor(status: number, problem: ProblemDetails, retryAfterSeconds: number | null = null) {
    super(problem.detail ?? problem.title ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.retryAfterSeconds = retryAfterSeconds;
    this.code = problem.code ?? DEFAULT_CODES[status] ?? (status >= 500 ? 'INTERNAL_ERROR' : 'HTTP_ERROR');
  }

  get title(): string | undefined {
    return this.problem.title;
  }

  get detail(): string | undefined {
    return this.problem.detail;
  }

  get diagnostics(): Diagnostic[] {
    return this.problem.diagnostics ?? [];
  }

  static async fromResponse(response: Response): Promise<ApiError> {
    let problem: ProblemDetails = { status: response.status, title: response.statusText || undefined };
    const contentType = response.headers.get('Content-Type') ?? '';
    if (contentType.includes('json')) {
      try {
        problem = { ...problem, ...((await response.json()) as ProblemDetails) };
      } catch {
        // Keep the status-only problem.
      }
    }
    const retryAfter = response.headers.get('Retry-After')?.trim() ?? '';
    const seconds = /^\d+$/.test(retryAfter) ? Number(retryAfter) : null;
    return new ApiError(response.status, problem, seconds);
  }
}

export function isApiError(error: unknown, code?: string): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code);
}

/** A response body together with the ETag needed for If-Match. */
export interface Versioned<T> {
  data: T;
  etag: string;
}

export const blueprintEtag = (latestVersion: number) => `"v${latestVersion}"`;
export const revisionEtag = (revision: number) => `"r${revision}"`;

export type AccessTokenProvider = () => Promise<string | null | undefined> | string | null | undefined;

export interface ApiClientOptions {
  /** Origin of the API, for example `https://nebisys.example.com`. Empty means the page's origin. */
  baseUrl?: string;
  getAccessToken?: AccessTokenProvider;
  fetch?: typeof fetch;
}

type Query = Record<string, string | number | null | undefined | readonly string[]>;

interface RequestOptions {
  body?: unknown;
  contentType?: string;
  ifMatch?: string;
  query?: Query;
  accept?: string;
  signal?: AbortSignal;
  /** The fetch cache mode (the reveal sends `no-store`). */
  cache?: RequestCache;
}

export class ApiClient {
  readonly baseUrl: string;
  private readonly getAccessToken?: AccessTokenProvider;
  private readonly fetchImpl: typeof fetch;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? '').replace(/\/+$/, '');
    this.getAccessToken = options.getAccessToken;
    const custom = options.fetch;
    this.fetchImpl = custom ?? ((input, init) => globalThis.fetch(input, init));
  }

  url(path: string, query?: Query): string {
    let url = `${this.baseUrl}${API_PREFIX}${path}`;
    if (query) {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(query)) {
        if (Array.isArray(value)) for (const item of value) params.append(key, item);
        else if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
      }
      const text = params.toString();
      if (text) url += `?${text}`;
    }
    return url;
  }

  async authHeaders(): Promise<Record<string, string>> {
    const token = await this.getAccessToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /** Fetch with the access token attached; used by the event stream. */
  readonly authorizedFetch: typeof fetch = async (input, init) => {
    const headers = new Headers(init?.headers);
    for (const [key, value] of Object.entries(await this.authHeaders())) headers.set(key, value);
    return this.fetchImpl(input, { ...init, headers });
  };

  private async send(method: string, path: string, options: RequestOptions = {}): Promise<Response> {
    const headers: Record<string, string> = {
      Accept: options.accept ?? 'application/json',
      ...(await this.authHeaders()),
    };
    let body: string | undefined;
    if (options.body !== undefined) {
      headers['Content-Type'] = options.contentType ?? 'application/json';
      body = JSON.stringify(options.body);
    }
    if (options.ifMatch) headers['If-Match'] = options.ifMatch;

    let response: Response;
    try {
      response = await this.fetchImpl(this.url(path, options.query), {
        method,
        headers,
        body,
        signal: options.signal,
        ...(options.cache ? { cache: options.cache } : {}),
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      connection.requestFailed();
      throw new ApiError(0, { title: 'The server could not be reached.', code: 'NETWORK_ERROR' });
    }
    connection.requestSucceeded();
    if (!response.ok) throw await ApiError.fromResponse(response);
    return response;
  }

  private async json<T>(method: string, path: string, options?: RequestOptions): Promise<T> {
    const response = await this.send(method, path, options);
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  private async versioned<T>(
    method: string,
    path: string,
    fallbackEtag: (data: T) => string,
    options?: RequestOptions,
  ): Promise<Versioned<T>> {
    const response = await this.send(method, path, options);
    const data = (await response.json()) as T;
    return { data, etag: response.headers.get('ETag') ?? fallbackEtag(data) };
  }

  private static page(request?: PageRequest): Query {
    return { limit: request?.limit, cursor: request?.cursor ?? undefined };
  }

  // ---- Shell ----

  getConfig(signal?: AbortSignal) {
    return this.json<AppConfig>('GET', '/config', { signal });
  }

  getMe(signal?: AbortSignal) {
    return this.json<Me>('GET', '/me', { signal });
  }

  /** The Nebisys version (spec 04, 6.13); not a blueprint version (`getVersion`). */
  getAppVersion() {
    return this.json<VersionInfo>('GET', '/version');
  }

  getChangelog() {
    return this.json<Changelog>('GET', '/version/changelog');
  }

  // ---- Blueprints ----

  listBlueprints(page?: PageRequest) {
    return this.json<Page<BlueprintListItem>>('GET', '/blueprints', { query: ApiClient.page(page) });
  }

  createBlueprint(content: BlueprintDocument) {
    return this.versioned<Blueprint>('POST', '/blueprints', (b) => blueprintEtag(b.latestVersion), {
      body: content,
    });
  }

  validateBlueprint(content: BlueprintDocument, signal?: AbortSignal) {
    return this.json<ValidationResult>('POST', '/blueprints/validate', { body: content, signal });
  }

  getBlueprint(id: string) {
    return this.versioned<Blueprint>('GET', `/blueprints/${id}`, (b) => blueprintEtag(b.latestVersion));
  }

  updateBlueprint(id: string, content: BlueprintDocument, etag: string) {
    return this.versioned<Blueprint>('PUT', `/blueprints/${id}`, (b) => blueprintEtag(b.latestVersion), {
      body: content,
      ifMatch: etag,
    });
  }

  deleteBlueprint(id: string, etag: string) {
    return this.json<void>('DELETE', `/blueprints/${id}`, { ifMatch: etag });
  }

  saveLayout(id: string, layout: Layout) {
    return this.json<void>('PUT', `/blueprints/${id}/layout`, { body: layout });
  }

  listVersions(id: string, page?: PageRequest) {
    return this.json<Page<BlueprintVersionSummary>>('GET', `/blueprints/${id}/versions`, {
      query: ApiClient.page(page),
    });
  }

  getVersion(id: string, version: number) {
    return this.json<BlueprintVersion>('GET', `/blueprints/${id}/versions/${version}`);
  }

  // ---- Deployments ----

  listDeployments(blueprintId: string, page?: PageRequest) {
    return this.json<Page<Deployment>>('GET', `/blueprints/${blueprintId}/deployments`, {
      query: ApiClient.page(page),
    });
  }

  /** `GET /deployments`: the deployments of every blueprint, newest first, with filters. */
  listAllDeployments(filter: DeploymentListFilter = {}, page?: PageRequest) {
    return this.json<Page<DeploymentListItem>>('GET', '/deployments', {
      query: { ...ApiClient.page(page), ...filter },
    });
  }

  createDeployment(blueprintId: string, request: CreateDeploymentRequest) {
    return this.versioned<Deployment>(
      'POST',
      `/blueprints/${blueprintId}/deployments`,
      (d) => revisionEtag(d.revision),
      { body: request },
    );
  }

  /**
   * The deployment dry run (`POST /blueprints/{id}/deployments/validate`, spec 4.4, A28): checks a name and full
   * settings against the latest version without saving; with `deploymentId` as that deployment's new settings.
   */
  validateDeployment(
    blueprintId: string,
    request: { name: string; settings: Partial<DeploymentSettings> },
    deploymentId?: string,
    signal?: AbortSignal,
  ) {
    return this.json<DeploymentValidation>('POST', `/blueprints/${blueprintId}/deployments/validate`, {
      body: request,
      query: deploymentId ? { deploymentId } : undefined,
      signal,
    });
  }

  getDeployment(id: string) {
    return this.versioned<Deployment>('GET', `/deployments/${id}`, (d) => revisionEtag(d.revision));
  }

  /** RFC 7396 merge patch of `{ name, settings }`. */
  patchDeployment(id: string, patch: JsonObject, etag: string) {
    return this.versioned<Deployment>('PATCH', `/deployments/${id}`, (d) => revisionEtag(d.revision), {
      body: patch,
      contentType: 'application/merge-patch+json',
      ifMatch: etag,
    });
  }

  /** `keepVaultSecrets`: delete although a namespace vault's secrets remain (feature spec 15). */
  deleteDeployment(id: string, etag: string, keepVaultSecrets = false) {
    return this.json<void>('DELETE', `/deployments/${id}`, {
      ifMatch: etag,
      query: keepVaultSecrets ? { keepVaultSecrets: 'true' } : undefined,
    });
  }

  getDeploymentResources(id: string) {
    return this.json<{ items: DeploymentResource[] }>('GET', `/deployments/${id}/resources`);
  }

  listRuns(deploymentId: string, page?: PageRequest) {
    return this.json<Page<Run>>('GET', `/deployments/${deploymentId}/runs`, { query: ApiClient.page(page) });
  }

  /** A deployment's runs as run summaries (`?view=summary`), with kind and status filters. */
  listRunSummaries(
    deploymentId: string,
    filter: Omit<RunListFilter, 'deploymentId'> = {},
    page?: PageRequest,
  ) {
    return this.json<Page<RunSummary>>('GET', `/deployments/${deploymentId}/runs`, {
      query: { ...ApiClient.page(page), status: filter.status, kind: filter.kind, view: 'summary' },
    });
  }

  /** `GET /runs`: run summaries of every deployment, newest first, with filters. */
  listAllRuns(filter: RunListFilter = {}, page?: PageRequest) {
    return this.json<Page<RunSummary>>('GET', '/runs', {
      query: {
        ...ApiClient.page(page),
        status: filter.status,
        kind: filter.kind,
        deploymentId: filter.deploymentId,
        target: filter.target,
        networkId: filter.networkId,
      },
    });
  }

  createRun(deploymentId: string, request: CreateRunRequest) {
    return this.json<Run>('POST', `/deployments/${deploymentId}/runs`, { body: request });
  }

  // ---- Secrets and settings (feature spec 12, 7.1; shapes owned by 14 Secrets service) ----

  /** `fresh` bypasses the server's listing and reference-status caches (rate-limited per user). */
  getSecrets(deploymentId: string, signal?: AbortSignal, fresh = false) {
    return this.json<SecretsOverview>('GET', `/deployments/${deploymentId}/secrets`, {
      signal,
      query: fresh ? { fresh: 'true' } : undefined,
    });
  }

  /** `GET /deployments/{id}/settings`: every app's variables (the Variables window). */
  getVariables(
    deploymentId: string,
    options: { resourceIds?: readonly string[]; fresh?: boolean; signal?: AbortSignal } = {},
  ) {
    return this.json<DeploymentVariables>('GET', `/deployments/${deploymentId}/settings`, {
      signal: options.signal,
      query: { resourceId: options.resourceIds, fresh: options.fresh ? 'true' : undefined },
    });
  }

  /** Writes a new version to the vault. The value is never returned by any endpoint. */
  setSecretValue(deploymentId: string, name: string, request: SetSecretRequest) {
    return this.json<SecretValueSetResult>(
      'PUT',
      `/deployments/${deploymentId}/secrets/${encodeURIComponent(name)}`,
      { body: request },
    );
  }

  acceptSecret(deploymentId: string, name: string) {
    return this.json<SecretMetadata>(
      'POST',
      `/deployments/${deploymentId}/secrets/${encodeURIComponent(name)}/accept`,
    );
  }

  ignoreSecret(deploymentId: string, name: string) {
    return this.json<SecretMetadata>(
      'POST',
      `/deployments/${deploymentId}/secrets/${encodeURIComponent(name)}/ignore`,
    );
  }

  rewriteSecretTags(deploymentId: string) {
    return this.json<SecretsOverview>('POST', `/deployments/${deploymentId}/secrets/rewrite-tags`);
  }

  /**
   * Opt-in reveal (spec 6.10.18): one plain request per reveal or copy, `cache: 'no-store'`, never retried and
   * never routed through TanStack Query. The result holds the value: callers keep it in component state only.
   */
  revealSecret(deploymentId: string, name: string, request: RevealRequest, signal?: AbortSignal) {
    return this.json<RevealedSecret>(
      'POST',
      `/deployments/${deploymentId}/secrets/${encodeURIComponent(name)}/reveal`,
      { body: request, signal, cache: 'no-store' },
    );
  }

  /** Restores a secret someone soft-deleted in the vault; the server waits up to a minute (504 `RECOVERY_PENDING`). */
  recoverSecret(deploymentId: string, name: string) {
    return this.json<SecretMetadata>(
      'POST',
      `/deployments/${deploymentId}/secrets/${encodeURIComponent(name)}/recover`,
    );
  }

  /** Rolls the app out (App Service restart, ECS new tasks, Cloud Run new revision; feature spec 15). */
  restartApp(deploymentId: string, resourceId: string) {
    return this.json<AppRestartResult>(
      'POST',
      `/deployments/${deploymentId}/resources/${encodeURIComponent(resourceId)}/restart`,
    );
  }

  /** Asks the app's platform to re-read its vault references now. */
  refreshReferences(deploymentId: string, resourceId: string) {
    return this.json<ResourceSettings>(
      'POST',
      `/deployments/${deploymentId}/resources/${encodeURIComponent(resourceId)}/refresh-references`,
    );
  }

  /** `GET /deployments/{id}/activity`: the deployment's activity, newest first, with filters. */
  listActivity(deploymentId: string, filter: ActivityFilter = {}, page?: PageRequest, signal?: AbortSignal) {
    return this.json<Page<ActivityEntry>>('GET', `/deployments/${deploymentId}/activity`, {
      signal,
      query: {
        ...ApiClient.page(page),
        kind: filter.kind?.length ? filter.kind.join(',') : undefined,
        secretName: filter.secretName,
        resourceId: filter.resourceId,
        actor: filter.actor,
      },
    });
  }

  getResourceSettings(deploymentId: string, resourceId: string, signal?: AbortSignal) {
    return this.json<ResourceSettings>(
      'GET',
      `/deployments/${deploymentId}/resources/${encodeURIComponent(resourceId)}/settings`,
      { signal },
    );
  }

  // ---- Runs ----

  getRun(id: string) {
    return this.json<Run>('GET', `/runs/${id}`);
  }

  /** The variable changes stored with a completed plan (spec 4.4, A16); 404 `NOT_RECORDED` for older plans. */
  getRunVariableChanges(id: string) {
    return this.json<RunVariableChanges>('GET', `/runs/${id}/variable-changes`);
  }

  cancelRun(id: string) {
    return this.json<Run>('POST', `/runs/${id}/cancel`);
  }

  getRunEvents(id: string, after = 0, limit = 1000) {
    return this.json<RunEventPage>('GET', `/runs/${id}/events`, { query: { after, limit } });
  }

  // ---- Catalog and providers ----

  listResourceTypes() {
    return this.json<{ items: ResourceType[] }>('GET', '/resource-types');
  }

  getResourceType(type: string) {
    return this.json<ResourceType>('GET', `/resource-types/${encodeURIComponent(type)}`);
  }

  listProviders() {
    return this.json<{ items: Provider[] }>('GET', '/providers');
  }

  listSecretStores() {
    return this.json<{ items: SecretStore[] }>('GET', '/secret-stores');
  }

  // ---- Cloud accounts ----

  listCloudAccounts(page?: PageRequest) {
    return this.json<Page<CloudAccount>>('GET', '/cloud-accounts', { query: ApiClient.page(page) });
  }

  createCloudAccount(request: CreateCloudAccountRequest) {
    return this.versioned<CloudAccount>('POST', '/cloud-accounts', (a) => revisionEtag(a.revision), {
      body: request,
    });
  }

  getCloudAccount(id: string) {
    return this.versioned<CloudAccount>('GET', `/cloud-accounts/${id}`, (a) => revisionEtag(a.revision));
  }

  updateCloudAccount(id: string, request: UpdateCloudAccountRequest, etag: string) {
    return this.versioned<CloudAccount>('PUT', `/cloud-accounts/${id}`, (a) => revisionEtag(a.revision), {
      body: request,
      ifMatch: etag,
    });
  }

  deleteCloudAccount(id: string, etag: string) {
    return this.json<void>('DELETE', `/cloud-accounts/${id}`, { ifMatch: etag });
  }

  verifyCloudAccount(id: string) {
    return this.json<VerificationResult>('POST', `/cloud-accounts/${id}/verify`);
  }

  // ---- Networks (feature spec 17, phase 1; spec 4.4 Networks) ----

  listNetworks(page?: PageRequest) {
    return this.json<Page<NetworkListItem>>('GET', '/networks', { query: ApiClient.page(page) });
  }

  createNetwork(request: CreateNetworkRequest) {
    return this.versioned<Network>('POST', '/networks', () => '', { body: request });
  }

  /** `POST /networks/validate`: the dry run of a new network (allocations, preselection); saves nothing. */
  validateNewNetwork(request: CreateNetworkRequest, signal?: AbortSignal) {
    return this.json<NetworkValidation>('POST', '/networks/validate', { body: request, signal });
  }

  getNetwork(id: string) {
    return this.versioned<Network>('GET', `/networks/${id}`, () => '');
  }

  /** RFC 7396 merge patch of `{ name, settings, cloudAccounts }` with `If-Match`. */
  patchNetwork(id: string, patch: PatchNetworkRequest, etag: string) {
    return this.versioned<Network>('PATCH', `/networks/${id}`, () => '', {
      body: patch,
      contentType: 'application/merge-patch+json',
      ifMatch: etag,
    });
  }

  /** `POST /networks/{id}/validate`: the patch's dry run with its `impact` (an empty patch: the current settings). */
  validateNetwork(id: string, patch: PatchNetworkRequest, signal?: AbortSignal) {
    return this.json<NetworkValidation>('POST', `/networks/${id}/validate`, { body: patch, signal });
  }

  deleteNetwork(id: string, etag: string) {
    return this.json<void>('DELETE', `/networks/${id}`, { ifMatch: etag });
  }

  getAddressPlan(id: string) {
    return this.json<AddressPlan>('GET', `/networks/${id}/address-plan`);
  }

  listAttachments(id: string, filter: AttachmentListFilter = {}, page?: PageRequest) {
    return this.json<Page<Attachment>>('GET', `/networks/${id}/attachments`, {
      query: {
        ...ApiClient.page(page),
        state: filter.state,
        provider: filter.provider,
        region: filter.region,
        q: filter.q,
      },
    });
  }

  listEligibleDeployments(id: string, page?: PageRequest) {
    return this.json<Page<EligibleDeployment>>('GET', `/networks/${id}/eligible-deployments`, {
      query: ApiClient.page(page),
    });
  }

  /** Retry of a failed attachment: 202 with the connect run. */
  connectAttachment(id: string, deploymentId: string) {
    return this.json<Run>('POST', `/networks/${id}/attachments/${deploymentId}/connect`);
  }

  getNetworkDns(id: string) {
    return this.json<NetworkDns>('GET', `/networks/${id}/dns`);
  }

  listNetworkRuns(id: string, page?: PageRequest) {
    return this.json<Page<RunSummary>>('GET', `/networks/${id}/runs`, { query: ApiClient.page(page) });
  }

  createNetworkRun(id: string, request: CreateNetworkRunRequest) {
    return this.json<Run>('POST', `/networks/${id}/runs`, { body: request });
  }

  listNetworkActivity(id: string, filter: ActivityFilter = {}, page?: PageRequest, signal?: AbortSignal) {
    return this.json<Page<ActivityEntry>>('GET', `/networks/${id}/activity`, {
      signal,
      query: {
        ...ApiClient.page(page),
        kind: filter.kind?.length ? filter.kind.join(',') : undefined,
        actor: filter.actor,
      },
    });
  }

  /** The kinds registry (spec 4.10); with `networkId` the `needs-provider` reasons for that network. */
  getNetworkKinds(networkId?: string | null) {
    return this.json<NetworkKinds>('GET', '/network-kinds', {
      query: networkId ? { networkId } : undefined,
    });
  }

  // ---- Entry points (feature spec 17, phase 3; spec 4.4 Entry points) ----

  /** An entry point's live status; `fresh` reads the cloud now instead of the 30-second cache. */
  getEntryPointStatus(id: string, epId: string, fresh = false) {
    return this.json<EntryPointStatus>(
      'GET',
      `/networks/${id}/entry-points/${encodeURIComponent(epId)}/status`,
      { query: fresh ? { fresh: 'true' } : undefined },
    );
  }

  /** Starts a rotation of the entry point's origin secrets (202 with the status; 409 `ORIGIN_ROTATION_PENDING`). */
  rotateOriginSecret(id: string, epId: string, request: RotateOriginSecretRequest) {
    return this.json<EntryPointStatus>(
      'POST',
      `/networks/${id}/entry-points/${encodeURIComponent(epId)}/rotate-origin-secret`,
      { body: request },
    );
  }

  // ---- Network requests (feature spec 17, phase 2; spec 4.4 Network requests) ----

  listNetworkRequests(id: string, filter: RequestListFilter = {}, page?: PageRequest) {
    return this.json<Page<RequestGroup>>('GET', `/networks/${id}/requests`, {
      query: {
        ...ApiClient.page(page),
        decision: filter.decision?.length ? filter.decision.join(',') : undefined,
        enforcement: filter.enforcement?.length ? filter.enforcement.join(',') : undefined,
        sourceBlueprintId: filter.sourceBlueprintId,
        targetBlueprintId: filter.targetBlueprintId,
        neededForMicroSegmentation: filter.neededForMicroSegmentation ? 'true' : undefined,
      },
    });
  }

  private static requestPath(id: string, key: RequestInstanceKey) {
    return `/networks/${id}/requests/${key.sourceDeploymentId}/${encodeURIComponent(key.requestId)}/${key.provider}/${key.region}`;
  }

  /** Approve, reject, revoke or restore one part of an instance (`If-Match: "v<version>"`). */
  decideNetworkRequest(
    id: string,
    key: RequestInstanceKey,
    decision: RequestDecisionKind,
    body: DecideRequestBody,
    version: number,
  ) {
    return this.json<RequestInstance>('POST', `${ApiClient.requestPath(id, key)}/${decision}`, {
      body,
      ifMatch: `"v${version}"`,
    });
  }

  /** One decision for many instances, all or nothing, with one decision run. */
  decideNetworkRequests(id: string, body: BulkDecideRequestBody) {
    return this.json<BulkDecideResponse>('POST', `/networks/${id}/requests/decide`, { body });
  }

  /** Designers and operators ask a network admin again about a rejected or revoked part. */
  askAgainNetworkRequest(id: string, key: RequestInstanceKey, body: DecideRequestBody, version: number) {
    return this.json<RequestInstance>('POST', `${ApiClient.requestPath(id, key)}/ask-again`, {
      body,
      ifMatch: `"v${version}"`,
    });
  }

  /** Retry a failed enforcement: 202 with the decision run. */
  enforceNetworkRequest(id: string, key: RequestInstanceKey, version: number) {
    return this.json<Run>('POST', `${ApiClient.requestPath(id, key)}/enforce`, { ifMatch: `"v${version}"` });
  }

  /** A retained deployment gives its network addresses back (feature spec 17). */
  releaseDeploymentNetwork(deploymentId: string) {
    return this.versioned<Deployment>('POST', `/deployments/${deploymentId}/network/release`, (d) =>
      revisionEtag(d.revision),
    );
  }
}

/** Fetches every page of a cursor-paged list (used for small lists such as cloud accounts). */
export async function fetchAllPages<T>(
  load: (page: PageRequest) => Promise<Page<T>>,
  limit = 200,
): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | null = null;
  do {
    const page: Page<T> = await load({ limit, cursor });
    items.push(...page.items);
    cursor = page.nextCursor;
  } while (cursor);
  return items;
}
